import { getLocalTimeZone, parseDate } from "@internationalized/date";
import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import { format, subDays } from "date-fns";
import { DayPlanTrackingModal } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/DayPlanTrackingModal";
import { TableContent } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableContent/TableContent";
import { filterDayPlanTrackings } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableContent/utils";
import { TableFilter } from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableFilter/TableFilter";
import {
  FilterType,
  initStates,
} from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/TableFilter/constants";
import {
  DayPlanTrackingResponseType,
  DayPlanTrackingType,
  TableColumns,
} from "features/CenterContent/RoleContent/Personellstyrer/DayPlanTrackings/types";
import { SharedGridStyle } from "features/CenterContent/shared/MainContentGrid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SegmentedPanel } from "shared/components/SegmentedPanel";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableColumnHeader,
  TableHeader,
  TableRow,
} from "shared/components/Spor";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { TableSkeleton } from "shared/components/feedback/SkeletonLoader/TableSkeleton";
import styled, { useTheme } from "styled-components";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  DayPlanTrackingMessage,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

const getDayplanTrackingFilterSettings = (): FilterType | null => {
  const parsed = JSON.parse(
    sessionStorage.getItem("dayplanTrackingFilterSettings") ?? "null",
  );
  return parsed
    ? { ...parsed, chosenDate: parseDate(parsed?.chosenDate) }
    : null;
};

const setSessionStorageDayplanTrackingFilterSettings = (
  dayplanTrackingFilterSettings: FilterType,
) => {
  const date = format(
    dayplanTrackingFilterSettings.chosenDate.toDate(getLocalTimeZone()),
    "yyyy-MM-dd",
  );

  sessionStorage.setItem(
    "dayplanTrackingFilterSettings",
    JSON.stringify({
      ...dayplanTrackingFilterSettings,
      chosenDate: date,
    }),
  );
};

const TableStyled = styled(Table)`
  /* Nested in spor, therefore directly affect Table instead */
  Table {
    margin-bottom: 10vh;
  }
`;

const DayPlanTrackings = () => {
  const theme = useTheme();
  const [filterParams, setFilterParams] = useState<FilterType>(
    getDayplanTrackingFilterSettings() ?? initStates,
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedDayPlanTracking, setSelectedDayPlanTracking] =
    useState<DayPlanTrackingType | null>();

  useEffect(() => {
    setSessionStorageDayplanTrackingFilterSettings(filterParams);
  }, [filterParams]);

  const selectedDate = format(
    filterParams.chosenDate.toDate(getLocalTimeZone()),
    "yyyy-MM-dd",
  );

  const queryKey = useMemo(
    () => ["dayPlanTrackings", selectedDate],
    [selectedDate],
  );

  const messageHandler = useCallback(
    ({ message }: DayPlanTrackingMessage) => {
      queryClient.setQueryData<DayPlanTrackingType[]>(queryKey, (prev) =>
        prev
          ? [
              message,
              ...prev.filter(
                (d) =>
                  !(
                    d.employeeId === message.employeeId &&
                    d.dayPlanDate === message.dayPlanDate
                  ),
              ),
            ]
          : [message],
      );
    },
    [queryKey],
  );

  useWebsocket(BackendIdentifier.DROPS_BACKEND, {
    [WebsocketTopic.DAY_PLAN_TRACKING]: messageHandler,
  });

  // One day earlier to capture shifts that span past midnight
  const selectedPrevDate = format(
    subDays(filterParams.chosenDate.toDate(getLocalTimeZone()), 1),
    "yyyy-MM-dd",
  );

  const { isPending, isSuccess, isError, data } = useQuery({
    queryKey,
    refetchOnWindowFocus: true,
    queryFn: ({ signal }) =>
      queryFnGET<DayPlanTrackingResponseType>({
        signal,
        url: `${getBackendUrl()}/day-plan/trackings?fromDate=${selectedPrevDate}&toDate=${selectedDate}`,
      }).then(
        (dayPlanTrackingResponse) => dayPlanTrackingResponse.dayPlanTrackings,
      ),
  });

  const filteredDayPlanTrackings = useMemo(
    () => filterDayPlanTrackings(data ?? [], filterParams),
    [data, filterParams],
  );

  if (isError) {
    return <FailureMessage />;
  }

  return (
    <SegmentedPanel panelStyle={SharedGridStyle} dashedLineVariant="main">
      <Stack>
        <TableFilter
          setFilterParams={setFilterParams}
          filterParams={filterParams}
        />
      </Stack>
      <Stack overflow="auto" gap="0" height="100%">
        {/* This box stops content from showing behind the radius of the TableHeader */}
        <Box w="100%" h="0px" position="sticky" top="-12px" zIndex="1">
          <Box w="100%" h="12px" bg={theme.colorBackgroundMain} />
        </Box>
        <TableStyled size="sm" display="flex" flexDirection="column">
          <TableHeader position="sticky" top="-12px" zIndex="2" width="100%">
            <TableRow>
              {TableColumns.map((columnTitle, index) => (
                <TableColumnHeader
                  style={{
                    borderRadius: `${index === 0 ? "12px" : "0px"} ${
                      index === TableColumns.length - 1 ? "12px" : "0px"
                    } 0px 0px`,
                    backgroundColor: theme.colorBackgroundSecondary,
                    border: "0px",
                    padding: "12px",
                    whiteSpace: "nowrap",
                    textAlign:
                      index === TableColumns.length - 1 ? "right" : "left",
                  }}
                  key={columnTitle}
                >
                  {columnTitle}
                </TableColumnHeader>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isPending && (
              <TableSkeleton
                numOfRows={10}
                numOfColumns={TableColumns.length}
              />
            )}

            {isSuccess && (
              <TableContent
                filteredDayPlanTrackings={filteredDayPlanTrackings}
                onRowClick={(dayPlanTracking: DayPlanTrackingType) => {
                  setSelectedDayPlanTracking(dayPlanTracking);
                  setModalOpen(true);
                }}
              />
            )}
          </TableBody>
        </TableStyled>
        {isSuccess && selectedDayPlanTracking && (
          <DayPlanTrackingModal
            showModal={modalOpen}
            closeModal={() => setModalOpen(false)}
            dayPlanTracking={selectedDayPlanTracking}
          />
        )}
      </Stack>
    </SegmentedPanel>
  );
};

export default withErrorBoundary(DayPlanTrackings);
